import type { BaseEntry, RawEntry } from '../models/panel-entry.model';
import type { PanelListData } from '../models/panel-response.model';
import { toRawEntry } from './panel-normalize.util';

/**
 * Gabungkan data resmi (`items`) dengan draft revisi (`revision_items`) menjadi SATU array final:
 * kondisi profil seandainya semua revisi disetujui.
 *
 * Vendor tidak pernah mengubah data resmi secara langsung — setiap perubahan masuk sebagai entri
 * revisi menunggu approval admin. Validator yang hanya membaca `items` salah dalam dua arah:
 * dokumen yang baru diunggah dianggap belum ada, dan baris yang sudah ditandai hapus masih
 * terhitung memenuhi syarat. Gejalanya khas: **user melihat form yang sudah dia isi, tapi badge
 * tetap ⚠️.**
 *
 * TIGA keputusan di sini tidak boleh diubah (KONSEP §4.3):
 * a. Dedup pakai `identUuid`, BUKAN `id` — `CREATE` belum punya baris resmi padanannya.
 * b. Revisi MENANG atas data resmi — itulah niat terakhir user.
 * c. `DELETE` dibuang DI SINI, bukan di tiap validator. Konsekuensinya: validator TIDAK PERLU
 *    (dan tidak boleh) menulis `filter(e => e.entryType !== 'DELETE')` lagi.
 *
 * Lihat plans/KONSEP-PANEL-VALIDATION.md §4.
 */
export function resolveEntities<T extends BaseEntry>(
  data: Pick<PanelListData, 'items' | 'revision_items'> | null | undefined,
  normalize: (raw: RawEntry) => T,
): T[] {
  // `null` diterima supaya kegagalan satu endpoint tidak menjatuhkan seluruh tab — pemanggil
  // cukup meneruskan apa adanya dan panelnya dinilai kosong (fail-safe → ⚠️).
  if (!data) return [];

  const revisions = (data.revision_items ?? []).map(toRawEntry).map(normalize);
  const items = (data.items ?? []).map(toRawEntry).map(normalize);

  // Baris tanpa `identUuid` tidak boleh jadi pasangan siapa pun — kalau `null` ikut masuk Set,
  // dua baris tak ber-uuid akan saling menghapus.
  const revisedUuids = new Set(
    revisions.map((r) => r.identUuid).filter((u): u is string => u !== null),
  );

  const merged = [
    ...revisions,
    ...items.filter((i) => i.identUuid === null || !revisedUuids.has(i.identUuid)),
  ];

  return merged.filter((e) => e.entryType !== 'DELETE');
}
